import { parseNavigationParams, parseProfessionalsListNavigationParams, QUERY_PARAM_TO_TAB } from '../utils/navigationHelpers.ts';

export type RouteKey =
  | 'dashboard'
  | 'peopleSearch'
  | 'projectDocCreate'
  | 'projectDocEdit'
  | 'productoCreate'
  | 'productoEdit'
  | 'marketplace'
  | 'marketplaceExplore'
  | 'workBag'
  | 'workBagExplore'
  | 'cotizacionesList'
  | 'cotizacionDetail'
  | 'cotizacionEdit'
  | 'cotizacionPostulante'
  | 'publicProjectsList'
  | 'publicProjectDetail'
  | 'obrasPrivadasExplore'
  | 'obrasPrivadasCreate'
  | 'obrasPrivadasDetail'
  | 'obrasPrivadasEdit'
  | 'costEstimation'
  | 'costEstimationDetail'
  | 'materialesCreate'
  | 'solicitudes'
  | 'usersList'
  | 'perfil'
  | 'workOffersInternal'
  | 'workOfferInternalDetail';

export interface RouteNode {
  key: RouteKey;
  pattern: string;
  label: string | ((params: Record<string, string>) => string);
  parent?: RouteKey | ((params: Record<string, string>) => RouteKey | undefined);
}

export const routeConfig: RouteNode[] = [
  { key: 'dashboard', pattern: '/dashboard', label: 'Inicio' },
  { key: 'projectDocCreate', pattern: '/documentacion-de-obra/crear', label: 'Crear documentación de obra', parent: 'dashboard' },
  { key: 'projectDocEdit', pattern: '/documentacion-de-obra/edit/:id', label: 'Editar documentación de obra', parent: 'dashboard' },
  { key: 'productoCreate', pattern: '/productos/crear', label: 'Crear producto o servicio', parent: 'dashboard' },
  { key: 'productoEdit', pattern: '/productos/edit/:id', label: 'Editar producto o servicio', parent: 'dashboard' },
  { key: 'marketplace', pattern: '/marketplace', label: 'Marketplace', parent: 'dashboard' },
  { key: 'marketplaceExplore', pattern: '/marketplace/explorar', label: 'Explorar marketplace', parent: 'dashboard' },
  { key: 'workBag', pattern: '/mercado-de-trabajo/crearoferta', label: 'Crear oferta de trabajo', parent: 'dashboard' },
  { key: 'workOffersInternal', pattern: '/bolsa-trabajo', label: 'Bolsa de trabajo', parent: 'dashboard' },
  { key: 'workOfferInternalDetail', pattern: '/bolsa-trabajo/:id', label: 'Detalle de oferta', parent: 'workOffersInternal' },
 
  { key: 'cotizacionDetail', pattern: '/cotizaciondemateriales/:id', label: 'Detalle de cotización', parent: 'cotizacionesList' },
  { key: 'cotizacionPostulante', pattern: '/cotizaciondemateriales/postulante/:quotationId', label: 'Cotizar materiales', parent: 'cotizacionesList' },
  { key: 'cotizacionEdit', pattern: '/cotizaciondemateriales/edit/:id', label: 'Editar cotización de materiales', parent: 'cotizacionesList' },
  { key: 'publicProjectDetail', pattern: '/proyecto/:id', label: 'Detalle de proyecto', parent: 'dashboard' },
  { key: 'obrasPrivadasExplore', pattern: '/obrasprivadas/explorar', label: 'Cotizar en obras', parent: 'dashboard' },
  { key: 'obrasPrivadasCreate', pattern: '/obrasprivadas/crear', label: 'Crear obra privada', parent: 'dashboard' },
  { key: 'obrasPrivadasEdit', pattern: '/obrasprivadas/edit/:id', label: 'Editar obra privada', parent: 'dashboard' },
  { key: 'obrasPrivadasDetail', pattern: '/obrasprivadas/:id', label: 'Detalle de obra privada', parent: 'dashboard' },
  { key: 'solicitudes', pattern: '/notificaciones', label: 'Notificaciones', parent: 'dashboard' },
  { key: 'perfil', pattern: '/perfil/:id', label: 'Perfil', parent: 'dashboard' },
  { key: 'publicProjectsList', pattern: '/obraspublicas', label: 'Obras públicas', parent: 'dashboard' },
  { key: 'costEstimation', pattern: '/cotizaciondemateriales/estimacion-costos', label: 'Estimación de costos', parent: 'dashboard' },
  { key: 'materialesCreate', pattern: '/cotizaciondemateriales/crear', label: 'Crear cotización de materiales', parent: 'dashboard' },
  { key: 'cotizacionesList', pattern: '/cotizaciondemateriales/explorar', label: 'Explorar cotizaciones de materiales', parent: 'dashboard' },
  { key: 'usersList', pattern: '/buscar-perfiles', label: 'Explorar perfiles', parent: 'dashboard' },
];

export const matchRoute = (pathname: string): { node: RouteNode; params: Record<string, string> }[] => {
  const segments = pathname.split('/').filter(Boolean);
  const matches: { node: RouteNode; params: Record<string, string> }[] = [];
  for (const node of routeConfig) {
    const patSegs = node.pattern.split('/').filter(Boolean);
    if (patSegs.length !== segments.length) continue;
    const params: Record<string, string> = {};
    let ok = true;
    for (let i = 0; i < patSegs.length; i++) {
      const p = patSegs[i];
      const s = `/${segments[i]}`;
      if (p.startsWith('/:')) {
        params[p.slice(2)] = segments[i];
        continue;
      }
      if (p !== s) {
        ok = false;
        break;
      }
    }
    if (ok) matches.push({ node, params });
  }
  if (matches.length) return matches;
  const roots = routeConfig.filter(r => r.pattern === '/dashboard');
  return roots.map(r => ({ node: r, params: {} }));
};

export const getParentForRoute = (
  node: RouteNode,
  pathname: string,
  searchParams: URLSearchParams
): RouteKey | undefined => {
  const navParams = parseNavigationParams(searchParams);
  const professionalsNav = parseProfessionalsListNavigationParams(searchParams);
  
  if (typeof node.parent === 'function') {
    const params: Record<string, string> = {};
    const segments = pathname.split('/').filter(Boolean);
    const patSegs = node.pattern.split('/').filter(Boolean);
    for (let i = 0; i < patSegs.length; i++) {
      const p = patSegs[i];
      if (p.startsWith(':')) {
        params[p.slice(1)] = segments[i];
      }
    }
    return node.parent(params);
  }

  if (professionalsNav && node.key === 'perfil') {
    return 'usersList';
  }

  if (navParams && navParams.from === 'perfil') {
    return 'perfil';
  }

  return node.parent;
};

export const getProfileBreadcrumbLabel = (searchParams: URLSearchParams): string => {
  const navParams = parseNavigationParams(searchParams);
  if (navParams && navParams.tab) {
    const tabName = QUERY_PARAM_TO_TAB[navParams.tab] || 'Perfil';
    return `${tabName} - Perfil`;
  }
  const tabParam = searchParams.get('tab');
  if (tabParam && QUERY_PARAM_TO_TAB[tabParam as keyof typeof QUERY_PARAM_TO_TAB]) {
    const tabName = QUERY_PARAM_TO_TAB[tabParam as keyof typeof QUERY_PARAM_TO_TAB];
    return `${tabName} - Perfil`;
  }
  return 'Perfil';
};


